import { PreventAndRedirectCommands, Router, RouterLocation } from "@vaadin/router";
import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { ToasterService } from "se-shared/services/toaster.service";
import { container } from "tsyringe";
import { LogLevel } from "../../enums/log-level";
import { ServerGroup } from "../../models/server-group";
import { AuthService } from "../../services/auth.service";
import { ConfigService } from "../../services/config.service";
import { ModalDialogService } from "../../services/modal-editor.service";
import { ProxyService } from "../../services/proxy.service";
import { RunService } from "../../services/run.service";
import { ServerGroupService } from "../../services/server-group.service";
import { UserState } from "../../services/user.state";
import { SubscriptionPlanApi } from "../../services/subscription-plan-api";
import "../components/cards/edit-form-card.element";
import "../components/cards/card-buttons.element";
import { FormSelectEditorElement } from "../editors/form/form-select-editor.element";
import { FormToggleEditorElement } from "../editors/form/form-toggle-editor.element";
import { SpaceService } from "../../services/space.service";
import { when } from "lit/directives/when.js";

@customElement("se-start-many-runs")
export class SeStartManyRunsElement extends LitElement {
    private _modalService: ModalDialogService;
    private _proxyService: ProxyService;
    private _authService: AuthService;
    private _runService: RunService;
    private _configService: ConfigService;
    private _toaterService: ToasterService;
    private _subscriptionPlanApi: SubscriptionPlanApi;
    private _userState: UserState;
    private _serverGroupService: ServerGroupService;
    private _spaceService: SpaceService;
    private _serverGroups: ServerGroup[];
    private _selectedProxyPoolId = -1;

    private _selectedIds: number[] = [];
    private _count: number;
    private _spaceId: number;
    private _labelId: number;
    private _templateId? = 0;
    private _returnUrl: string;
    private _hasAllProxies = false;
    @state() private _isProxyRequired = true;

    @state() private _proxyPools: any[] = [];
    @state() private _spaceName: string;
    @state() private _isLoading = true;

    @query("#server") private _serverEditor: FormSelectEditorElement;
    @query("#proxyPools") private _proxyPoolsSelect: FormSelectEditorElement;
    @query("#exclusive") private _exclusiveEditor: FormToggleEditorElement;
    @query("#waitOnFailure") private _waitOnFailureEditor: FormToggleEditorElement;
    @query("#logLevel") private _logLevelEditor: FormSelectEditorElement;

    private _logLevels = Object.entries(LogLevel).map(([value]) => ({ id: value, name: value }));

    constructor() {
        super();
        this._authService = container.resolve(AuthService);
        this._userState = container.resolve(UserState);
        this._configService = container.resolve(ConfigService);
        this._toaterService = container.resolve(ToasterService);
        this._runService = container.resolve(RunService);
        this._proxyService = container.resolve(ProxyService);
        this._modalService = container.resolve(ModalDialogService);
        this._serverGroupService = container.resolve(ServerGroupService);
        this._subscriptionPlanApi = container.resolve(SubscriptionPlanApi);
        this._spaceService = container.resolve(SpaceService);
    }

    async onBeforeEnter(location: RouterLocation, commands: PreventAndRedirectCommands, router: Router) {
        this._isLoading = true;
        if (!this._authService.isUser) {
            return commands.redirect("/login");
        }

        if (location.params.spaceId) {
            this._spaceId = parseInt(location.params.spaceId.valueOf() as string);
            if (this._userState.selectedSpaceId !== this._spaceId) {
                this._userState.selectedSpaceId = this._spaceId;
            }
        } else {
            this._toaterService.showError("Space ID not found in URL.");
            return commands.prevent();
        }

        if (location.params.labelId) {
            this._labelId = parseInt(location.params.labelId.valueOf() as string);
            if (this._userState.selectedLabelId !== this._labelId) {
                 this._userState.selectedLabelId = this._labelId;
            }
        } else {
            this._toaterService.showError("Label ID not found in URL.");
            return commands.prevent();
        }

        if (location.params.templateId) {
            this._templateId = parseInt(location.params.templateId.valueOf() as string);
        } else {
            this._templateId = undefined;
        }

        this._returnUrl = `space/${this._spaceId}/label/${this._labelId}` + (this._templateId ? `/template/${this._templateId}` : ``);

        if (this._userState.selectedIds && this._userState.selectedIds.length > 0) {
            this._selectedIds = [...this._userState.selectedIds];
            this._count = this._selectedIds.length;
        } else {
            this._toaterService.showUnexpectedError("No selected agents.");
            Router.go(this._returnUrl);
            return commands.prevent();
        }

        try {
            if (this._spaceId === 0) {
                this._spaceName = "Personal";
            } else {
                const spaceResult = await this._spaceService.api.getAsync(this._spaceId);
                if (spaceResult.isOk) {
                    this._spaceName = spaceResult.value.name;
                } else {
                    this._spaceName = "Space";
                    this._toaterService.showNetworkError(spaceResult.err);
                }
            }
        } catch (error) {
             console.error("Error fetching space name for breadcrumbs:", error);
             this._toaterService.showError("Failed to load page details.");
        }

        this._serverGroups = [
            { id: 0, name: "Shared", description: "" },
            { id: -this._authService.user.organizationId, name: "Default", description: "" }
        ];
        if (this._authService.isOrgAdmin) {
            const serverGroupResult = await this._serverGroupService.api.getAllAsync();
            if (serverGroupResult.isOk) {
                 const fetchedGroups = serverGroupResult.value.serverGroups;
                 this._serverGroups = [
                    { id: -this._authService.user.organizationId, name: "Default", description: "" },
                    { id: 0, name: "Shared", description: "" },
                    ...fetchedGroups
                ];
            } else {
                this._toaterService.showNetworkError(serverGroupResult.err);
                commands.prevent();
                return;
            }
        }

        const hasAllProxies = await this._configService.api.hasAllProxies(this._selectedIds);
        if (hasAllProxies.isOk) {
            this._hasAllProxies = hasAllProxies.value.hasProxies;
            this._isProxyRequired = this._authService.orgSettings.isProxyRequired && !this._hasAllProxies;
        }
        else {
            this._toaterService.showNetworkError(hasAllProxies.err);
            commands.prevent();
            return;
        }

        const proxyPoolsResults = await this._proxyService.api.getAllProxyPoolsWithSharedAsync(-1, -1, "null", "name", 1);
        if (proxyPoolsResults.isOk) {
            this._proxyPools = proxyPoolsResults.value.proxyPools;
            this._proxyPools.sort((a, b) => (a.name > b.name ? 1 : -1));

            if (this._isProxyRequired && this._proxyPools.length > 0) {
                this._selectedProxyPoolId = this._proxyPools[0].id;
            } else {
                this._selectedProxyPoolId = -1;
            }

        } else {
            this._toaterService.showNetworkError(proxyPoolsResults.err);
            commands.prevent();
            return;
        }
        this._isLoading = false;
    }

    async onAfterEnter(location: RouterLocation, commands: PreventAndRedirectCommands, router: Router) {}

    connectedCallback() {
        super.connectedCallback();
        document.title = "Start Many Runs";
    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }

    async RunAsync(ev?) {
        if (!this._isProxyRequired && (!this._selectedProxyPoolId || this._selectedProxyPoolId == -1) && !this._hasAllProxies) {
            const dialogRes = await this._modalService.openConfirmDialogAsync({ saveCaption: "Run Now", title: "Run Without Proxy?", body: "Are you sure you want to run these agents without setting a proxy? Some agents have no default proxies, so they will run without proxies." })
            if (!dialogRes.isSave) {
                return;
            }
        }

        const groupId = parseInt(this._serverEditor.liveValue);
        const run = {
            configIds: this._selectedIds,
            labelId: this._labelId,
            count: this._count,
            proxyPoolId: this._selectedProxyPoolId !== -1 ? this._selectedProxyPoolId : undefined,
            serverGroupId: groupId > 0 ? groupId : undefined,
            serverOrganizationId: groupId < 0 ? -groupId : undefined,
            isExclusive: this._exclusiveEditor.liveValue,
            isWaitOnFailure: this._waitOnFailureEditor.liveValue,
            logLevel: this._logLevelEditor.liveValue as LogLevel,
        };
        const result = await this._runService.api.createManyAsync(run);
        if (!result.isOk) {
            this._toaterService.showNetworkError(result.err);
        } else {
            Router.go(this._returnUrl);
        }
    }

    cancel() {
        Router.go(this._returnUrl);
    }
    selectProxyPoolId() {
        this._selectedProxyPoolId = Number(this._proxyPoolsSelect.liveValue);
        this.requestUpdate();
    }

    render() {
        const proxyPools = this._proxyPools.map((p) => ({ id: p.id, name: p.name }));
        const noneOption = [{ id: -1, name: "None" }];
        const proxyPoolOptions = this._isProxyRequired ? proxyPools : noneOption.concat(proxyPools);

        const selectedProxyPool = this._proxyPools.find(pool => pool.id === this._selectedProxyPoolId);
        const proxyCost = selectedProxyPool?.displayPrice != null ? selectedProxyPool.displayPrice.toFixed(2) : "0.00";

        const breadcrumbBase = html`
            <a href="/org/spaces">Spaces</a>
            <span>/</span>
            <a href="/space/${this._spaceId}">${this._spaceName ?? '...'}</a>
        `;

        return html`
            <se-loading-panel .isLoading=${this._isLoading}>
                 <div class="breadcrumbs">
                     ${breadcrumbBase}
                     <span>/</span>
                     <a>Start ${this._count} Agents</a>
                 </div>
                 <div class="h2">Start ${this._count} Agents</div>

                <div style="display: flex; flex-direction: column; gap: 15px;">
                    <se-edit-form-card cardTitle="Run Options">
                        <se-form-select-editor
                            id="server"
                            name="server"
                            label="Server group"
                            labelHelp="Select the server group to run the agents on"
                            .options=${this._serverGroups}
                            .value="${-this._authService.user.organizationId}"
                        ></se-form-select-editor>

                         <se-form-select-editor
                            id="proxyPools"
                            name="Proxy Pools"
                            label="Proxy"
                            labelHelp="Select the proxy pool to use. Cost: $${proxyCost} per GB"
                            @valueChanged=${this.selectProxyPoolId}
                            .options=${proxyPoolOptions}
                            .value=${this._selectedProxyPoolId.toString()}
                            ?required=${this._isProxyRequired}
                        >
                        </se-form-select-editor>

                        <se-form-select-editor
                            id="logLevel"
                            name="logLevel"
                            label="Log level"
                            labelHelp="Select the minimum level of detail for logs"
                            .value="${LogLevel.Info}"
                            .options=${this._logLevels}
                        ></se-form-select-editor>

                         <se-form-toggle-editor
                            id="exclusive"
                            ?value=${true}
                            label="Run one at a time per agent"
                            labelHelp="Prevent other runs of the *same* agent while its run is active"
                        ></se-form-toggle-editor>

                         <se-form-toggle-editor
                            id="waitOnFailure"
                            ?value=${false}
                            label="Wait on failure"
                            labelHelp="If a run fails, wait for others before marking the overall job failed"
                        ></se-form-toggle-editor>

                    </se-edit-form-card>

                     <se-card-buttons>
                        <se-primary-button .action="${() => this.RunAsync()}" text="Run ${this._count} Agents"></se-primary-button>
                        <se-secondary-button @click="${this.cancel}" text="Cancel"></se-secondary-button>
                    </se-card-buttons>
                </div>
            </se-loading-panel>
        `;
    }

    static styles = css`
        :host {
            display: flex;
            flex-direction: column;
            padding: 15px;
            box-sizing: border-box;
            gap: 15px;
            height: 100%;
        }
         .breadcrumbs {
            display: flex;
            flex-direction: row;
            gap: 10px;
            font: var(--font-smaller);
            margin-bottom: 20px;
        }
        .breadcrumbs a {
            color: var(--color-primary);
            text-decoration: none;
        }
        .breadcrumbs a:hover {
            text-decoration: underline;
        }
        .h2 {
            font: var(--font-h2);
            font-weight: 600;
            margin-bottom: 10px;
        }

        se-edit-form-card {
            width: 100%;
        }

         se-card-buttons {
             margin-top: 5px;
             padding: 0;
         }

         se-edit-form-card se-form-select-editor,
         se-edit-form-card se-form-toggle-editor {
         }
    `;
}

